import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, UserCog, Shield, PackageX } from 'lucide-react';
import * as Icons from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Verfügbare Icons
const AVAILABLE_ICONS = [
  'PackageX', 'Trash2', 'AlertCircle', 'XCircle', 'MinusCircle',
  'Archive', 'Ban', 'TrendingDown', 'ShoppingCart', 'Box'
];

// Verfügbare Farben
const AVAILABLE_COLORS = [
  { name: 'Rot', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Gelb', value: '#eab308' },
  { name: 'Grün', value: '#22c55e' },
  { name: 'Blau', value: '#3b82f6' },
  { name: 'Lila', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Grau', value: '#6b7280' },
];

export const AllgemeinTypen = () => {
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]); // NEW: Locations
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deletingType, setDeletingType] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    icon: 'PackageX',
    color: '#ef4444',
    enabledForAdmin: true,
    enabledForEmployee: true,
    selectedLocations: {}, // NEW: {location_id: true/false}
  });

  useEffect(() => {
    fetchTypes();
    fetchLocations(); // NEW: Fetch locations
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/locations`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setLocations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error('Fehler beim Laden der Standorte');
    }
  };

  const fetchTypes = async () => {
    try {
      const response = await axios.get(`${API}/api/general-writeoff-types`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch types:', error);
      toast.error('Fehler beim Laden der Typen');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      
      // Build selectedLocations object from location_ids
      const selectedLocs = {};
      (type.location_ids || []).forEach(locId => {
        selectedLocs[locId] = true;
      });
      
      setFormData({
        name: type.name,
        icon: type.icon,
        color: type.color,
        enabledForAdmin: type.enabled_for_roles.includes('admin'),
        enabledForEmployee: type.enabled_for_roles.includes('employee'),
        selectedLocations: selectedLocs,
      });
    } else {
      setEditingType(null);
      
      // Default: All locations selected
      const allSelected = {};
      locations.forEach(loc => {
        allSelected[loc.id] = true;
      });
      
      setFormData({
        name: '',
        icon: 'PackageX',
        color: '#ef4444',
        enabledForAdmin: true,
        enabledForEmployee: true,
        selectedLocations: allSelected,
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    const enabledForRoles = [];
    if (formData.enabledForAdmin) enabledForRoles.push('admin');
    if (formData.enabledForEmployee) enabledForRoles.push('employee');

    if (enabledForRoles.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Rolle aus');
      return;
    }

    // Build location_ids array from selectedLocations
    const location_ids = Object.keys(formData.selectedLocations)
      .filter(locId => formData.selectedLocations[locId]);

    if (location_ids.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Standort aus');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        icon: formData.icon,
        color: formData.color,
        enabled_for_roles: enabledForRoles,
        location_ids: location_ids, // NEW: Send location_ids
        customer_id: 'dummy' // Backend setzt die richtige customer_id
      };

      if (editingType) {
        await axios.put(`${API}/api/general-writeoff-types/${editingType.id}`, payload, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Typ erfolgreich aktualisiert');
      } else {
        await axios.post(`${API}/api/general-writeoff-types`, payload, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Typ erfolgreich erstellt');
      }

      setShowDialog(false);
      fetchTypes();
    } catch (error) {
      console.error('Failed to save type:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async () => {
    if (!deletingType) return;

    try {
      await axios.delete(`${API}/api/general-writeoff-types/${deletingType.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Typ erfolgreich gelöscht');
      setShowDeleteDialog(false);
      setDeletingType(null);
      fetchTypes();
    } catch (error) {
      console.error('Failed to delete type:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const renderIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent className="h-5 w-5" /> : <Icons.PackageX className="h-5 w-5" />;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Lädt...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 pb-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              Abschrift-Typen
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Verwalten Sie die Kategorien für allgemeine Abschriften
            </p>
          </div>
          <Button onClick={() => handleOpenDialog()} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            Neuer Typ
          </Button>
        </div>

        {/* Types Grid */}
        {types.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <PackageX className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Noch keine Typen</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Erstellen Sie Ihren ersten Abschrift-Typ
              </p>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Typ erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {types.map((type) => (
              <Card key={type.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${type.color}20` }}
                      >
                        <div style={{ color: type.color }}>
                          {renderIcon(type.icon)}
                        </div>
                      </div>
                      <div>
                        <CardTitle className="text-base sm:text-lg">{type.name}</CardTitle>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Roles */}
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Berechtigungen</Label>
                      <div className="flex flex-wrap gap-2">
                        {type.enabled_for_roles.includes('admin') && (
                          <Badge variant="secondary" className="text-xs">
                            <Shield className="h-3 w-3 mr-1" />
                            Admin
                          </Badge>
                        )}
                        {type.enabled_for_roles.includes('employee') && (
                          <Badge variant="secondary" className="text-xs">
                            <Users className="h-3 w-3 mr-1" />
                            Mitarbeiter
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Locations */}
                    {type.location_ids && type.location_ids.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Standorte</Label>
                        <div className="flex flex-wrap gap-1">
                          {type.location_ids.map((locId) => {
                            const location = locations.find(l => l.id === locId);
                            return location ? (
                              <Badge key={locId} variant="outline" className="text-xs">
                                📍 {location.name}
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDialog(type)}
                        className="flex-1"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Bearbeiten
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDeletingType(type);
                          setShowDeleteDialog(true);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Typ bearbeiten' : 'Neuer Abschrift-Typ'}
            </DialogTitle>
            <DialogDescription>
              Definieren Sie einen neuen Typ für allgemeine Abschriften
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="z.B. Privatverbrauch, Beschädigt..."
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {/* Icon */}
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="grid grid-cols-5 gap-2">
                {AVAILABLE_ICONS.map((iconName) => {
                  const IconComponent = Icons[iconName];
                  return (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon: iconName })}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        formData.icon === iconName
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <IconComponent className="h-5 w-5 mx-auto" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color */}
            <div className="space-y-2">
              <Label>Farbe</Label>
              <div className="grid grid-cols-4 gap-2">
                {AVAILABLE_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.color === color.value
                        ? 'border-gray-800 scale-105'
                        : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: color.value }}
                  >
                    <span className="sr-only">{color.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Roles */}
            <div className="space-y-3">
              <Label>Berechtigt für *</Label>
              
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium">Administratoren</span>
                </div>
                <Switch
                  checked={formData.enabledForAdmin}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, enabledForAdmin: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium">Mitarbeiter</span>
                </div>
                <Switch
                  checked={formData.enabledForEmployee}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, enabledForEmployee: checked })
                  }
                />
              </div>

            {/* NEW: Locations Multi-Select */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Label>📍 Verfügbar für Standorte</Label>
                <span className="text-xs text-gray-500">
                  ({Object.values(formData.selectedLocations).filter(Boolean).length} ausgewählt)
                </span>
              </div>
              
              {locations.length === 0 ? (
                <p className="text-sm text-gray-500">Keine Standorte gefunden</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  {locations.map((location) => (
                    <label 
                      key={location.id}
                      className="flex items-start gap-3 p-2 rounded hover:bg-white dark:bg-gray-900 transition-colors cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedLocations[location.id] || false}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            selectedLocations: {
                              ...formData.selectedLocations,
                              [location.id]: e.target.checked
                            }
                          });
                        }}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{location.name}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {location.street} {location.house_number}, {location.postal_code} {location.city}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <span>💡</span>
                <span>Dieser Typ ist für alle gewählten Standorte verfügbar</span>
              </p>
            </div>

            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave}>
              {editingType ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Typ löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Typ "{deletingType?.name}" wirklich löschen? 
              Bestehende Abschriften bleiben erhalten, aber Sie können keine neuen mehr mit diesem Typ erstellen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingType(null)}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};
